// Мост между React-клиентом и vanilla-движком (../src/engine).
// Vanilla-движок написан на ES-модулях без типов — здесь мы импортируем
// нужные функции и оборачиваем в типизированный API.
//
// При переезде на новый стек (диздок 16.7) движок остаётся как-есть:
// этот файл — единственная точка сцепки React + engine.

// JS-модуль движка без .d.ts. TypeScript смотрит JSDoc-аннотации и иногда
// выводит лишние ограничения. Каст к `any` снимает шум — реальные типы
// движка опишем в .d.ts когда переедем на TypeScript-engine.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import * as _engine from '@engine/match.js';
const engine = _engine as any;

// Bot AI. Этап 66: УМНЫЙ бот (bot-smart — дроп-ин поверх bot-random).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import * as _bot from '@engine/../ai/bot-smart.js';
const bot = _bot as any;
// Этап 66.2: синергия-колоды + выбор сильного Героя для PvE-бота.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import * as _botDecks from '@engine/../ai/bot-decks.js';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const botDecks = _botDecks as any;

export type Faction = 'deus' | 'syntetics' | 'global' | 'livlife' | 'neutral';

export interface MatchOptions {
  seed?: number;
  playerName?: string;
  botName?: string;
  playerFaction?: Faction;
  botFaction?: Faction;
  playerHeroId?: string;
  botHeroId?: string;
  playerDeck?: unknown[];
  botDeck?: unknown[];
  botRandom?: boolean;
}

// Тип состояния матча — пока `any`, реальный shape сложен (Player, Card, Hero...).
// Подменим на нормальные типы в следующих этапах.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Match = any;

export const PHASE = engine.PHASE as Record<string, string>;

export function createMatch(opts: MatchOptions): Match {
  return engine.createMatch(opts);
}

// ────────────── Мулиган + переход фаз ──────────────

// indices — индексы карт в руке (0..4), которые игрок хочет ВЕРНУТЬ
// в низ колоды + взять столько же сверху. Максимум 3.
// playerIdx: 0 = игрок (нижний), 1 = бот.
export function applyMulligan(state: Match, playerIdx: 0 | 1, indices: number[]): void {
  return engine.applyMulligan(state, playerIdx, indices);
}

export function finishMulligan(state: Match): void {
  return engine.finishMulligan(state);
}

// ────────────── Размещение в Ф2 ──────────────

// payload: { cardInstanceId, line, slot, targetInstanceId? } или null (сбросить все).
export function setPendingPlacement(
  state: Match,
  playerIdx: 0 | 1,
  // Юнит/Действие → { cardInstanceId, line, slot }; Мод → { cardInstanceId,
  // targetInstanceId } (line/slot не нужны — Мод вешается на юнит, не на клетку).
  payload: { cardInstanceId: number; line?: 0 | 1; slot?: number; targetInstanceId?: number } | null
): void {
  return engine.setPendingPlacement(state, playerIdx, payload);
}

// БУГ.2: placement-prompt — нужен ли picker для размещаемой карты.
// Возвращает один из:
//   { kind: 'prompt', prompt: { type, cardInstanceId, ... }, opts?: ... }
//   { kind: 'opts',   opts: { ... } }
//   { kind: 'none' }
//
// БУГ.5-fix5: determinePlacementPrompt НЕ реэкспортируется из match.js,
// поэтому engine.determinePlacementPrompt === undefined. Импортим напрямую
// из placement-prompts.js (alias @engine уже настроен в vite).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import { determinePlacementPrompt as _determinePlacementPrompt } from '@engine/abilities/placement-prompts.js';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function determinePlacementPrompt(state: Match, playerIdx: 0 | 1, card: any, line: 0 | 1): any {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (_determinePlacementPrompt as any)(state, playerIdx, card, line);
}

// БУГ.2: сохранить orderOpts для конкретной карты в pendingOrderOpts.
// В Ф3 при revel runUnitOrdersOnReveal вытащит эти opts и передаст в
// order-функцию (onixOrder и т.п.). opts может содержать любые поля,
// которые понимает конкретный order — для Оникса это {target: enemyUnit}.
export function setPendingOrderOpts(
  state: Match,
  playerIdx: 0 | 1,
  cardInstanceId: number,
  opts: Record<string, unknown>
): void {
  if (!state.pendingOrderOpts) state.pendingOrderOpts = [{}, {}];
  if (!state.pendingOrderOpts[playerIdx]) state.pendingOrderOpts[playerIdx] = {};
  state.pendingOrderOpts[playerIdx][cardInstanceId] = opts;
}

// БУГ.3: приоритеты атаки для Глобала. Каждый Глобал-юнит при размещении
// требует выбора приоритета (strongest / weakest / healer / random).
// Выбор фиксируется до конца игры в card.attackPriority.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import * as _priorityMod from '@engine/abilities/global/priority.js';
const priorityMod = _priorityMod as any;

export interface GlobalPriorityDef {
  id: 'strongest' | 'weakest' | 'healer' | 'random';
  label_ru: string;
  label_en: string;
  hint_ru: string;
  hint_en: string;
}
export const GLOBAL_PRIORITIES: readonly GlobalPriorityDef[] = priorityMod.GLOBAL_PRIORITIES;

// Нужен ли picker приоритета атаки для этой карты при размещении.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function needsAttackPriorityPicker(card: any): boolean {
  return !!priorityMod.needsAttackPriorityPicker(card);
}

// Записать выбранный приоритет на карту. Engine читает card.attackPriority
// в priorities.js applyPriority при выборе цели атаки.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function setUnitAttackPriority(card: any, priority: GlobalPriorityDef['id']): void {
  if (!card) return;
  if (!priorityMod.isValidGlobalPriority(priority)) return;
  card.attackPriority = priority;
}

// 2d: приоритеты ЛЕЧЕНИЯ для хилеров (аналог приоритета атаки Глобала).
// Хилер при размещении требует выбора, КОГО лечить (самый раненый / сильный /
// лекарь / случайный). Выбор фиксируется до конца игры в card.healPriority.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import * as _healPriorityMod from '@engine/heal-priority.js';
const healPriorityMod = _healPriorityMod as any;

export interface HealPriorityDef {
  id: 'lowest_hp' | 'strongest' | 'healer' | 'random';
  label_ru: string;
  label_en: string;
  hint_ru: string;
  hint_en: string;
}
export const HEAL_PRIORITIES: readonly HealPriorityDef[] = healPriorityMod.HEAL_PRIORITIES;

// Нужен ли picker приоритета лечения для этой карты при размещении.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function needsHealPriorityPicker(card: any): boolean {
  return !!healPriorityMod.needsHealPriorityPicker(card);
}

// Записать выбранный приоритет лечения. Engine читает card.healPriority
// в heal-priority.js getHealTarget при выборе цели лечения.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function setUnitHealPriority(card: any, priority: HealPriorityDef['id']): void {
  if (!card) return;
  if (!healPriorityMod.isValidHealPriority(priority)) return;
  card.healPriority = priority;
}

// Отменить одну запланированную выкладку. Возвращает true, если что-то снялось.
export function cancelPendingPlacement(
  state: Match,
  playerIdx: 0 | 1,
  cardInstanceId: number
): boolean {
  return engine.cancelPendingPlacement(state, playerIdx, cardInstanceId);
}

// Можно ли ещё разместить эту карту с учётом лимитов фракции/бюджета.
// Возвращает { ok: bool, reason?: string }.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function canAddPlacement(state: Match, playerIdx: 0 | 1, card: any): { ok: boolean; reason?: string } {
  return engine.canAddPlacement(state, playerIdx, card);
}

// Применить ожидающие выкладки обоих игроков и запустить Фазу 3 (raveal).
// anim — обычно null/undefined для headless.
export async function commitPlacementsAndReveal(state: Match, anim?: unknown): Promise<void> {
  return engine.commitPlacementsAndReveal(state, anim);
}

// ────────────── Действия (Actions) ──────────────

// 2d reveal-pick: peek N случайных Модов из колоды (для UI-picker'а Дозаправки/
// Конструктора в PvE). НЕ удаляет — резолвер в Ф3 разложит по opts. В PvP это
// делает сервер (анти-чит RNG), здесь — локальный движок.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import { revealModsFromDeck as _revealModsFromDeck } from '@engine/abilities/syntetics/c1/actions-c1.js';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function revealModsFromDeck(state: Match, playerIdx: 0 | 1, count: number): any[] {
  return _revealModsFromDeck(state, playerIdx, count) || [];
}

// Нужна ли цель для разыгрывания данного Действия.
export function actionNeedsTarget(cardId: string): boolean {
  return !!engine.actionNeedsTarget(cardId);
}

// Валидные цели для Действия (массив Card).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getActionTargets(state: Match, playerIdx: 0 | 1, cardId: string): any[] {
  return engine.getActionTargets(state, playerIdx, cardId) || [];
}

// Установить pendingAction игроку (карта в руке + опц. цель).
// Действие будет разыграно в Ф3 (commitPlacementsAndReveal).
export function setPlayerPendingAction(
  state: Match,
  playerIdx: 0 | 1,
  cardInstanceId: number,
  targetInstanceId: number | null = null,
  opts: Record<string, unknown> = {}
): void {
  state.players[playerIdx].pendingAction = {
    cardInstanceId,
    targetInstanceId,
    opts
  };
}

export function clearPlayerPendingAction(state: Match, playerIdx: 0 | 1): void {
  state.players[playerIdx].pendingAction = null;
}

// ────────────── Атаки Героя (Ф4/Ф5) ──────────────

// Применить атаку Героя текущего активного игрока. target = Card (юнит/Герой)
// или null (пропуск). После: фаза продвигается (Ф4→Ф5→конец хода).
// SYNC, без анимаций — используется ботом и тестами. Для UI см. Animated-версию.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function heroAttackOrSkip(state: Match, target: any | null): void {
  return engine.heroAttackOrSkip(state, target);
}

// БОЙ.1.9: async-версия с анимацией для UI/PvP.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function heroAttackOrSkipAnimated(state: Match, target: any | null, anim?: any): Promise<void> {
  return engine.heroAttackOrSkipAnimated(state, target, anim);
}

// Атака Героя БЕЗ продвижения фазы (для активации Стелс-юнитов
// в той же Ф4/Ф5). SYNC, без анимаций.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function performHeroAttackKeepPhase(state: Match, target: any): boolean {
  return engine.performHeroAttackKeepPhase(state, target);
}

// БОЙ.1.9: async-версия с анимацией для UI/PvP.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function performHeroAttackKeepPhaseAnimated(state: Match, target: any, anim?: any): Promise<boolean> {
  return engine.performHeroAttackKeepPhaseAnimated(state, target, anim);
}

// Проверка конца матча. Возвращает true, если matchOver установлен.
export function checkMatchOver(state: Match): boolean {
  return engine.checkMatchOver(state);
}

// Можно ли Герою атаковать Героя врага? Проверяет Авангард (диздок §6.5).
// Авангард — это «видимые» юниты на стороне врага. НЕ считаются:
//   • Стелс-юниты (диздок §6.7: стелс невидим → не в Авангарде).
//   • Спящий агент в planted/armed состоянии (диздок 7.1: карта-бомба).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function canHeroAttackHero(state: Match, attackerIdx: 0 | 1): boolean {
  const enemy = state.players[1 - attackerIdx];
  const enemyUnits = enemy.unitsOnField();
  const blocking = enemyUnits.filter((u: any) => {
    if (!u || !u.alive) return false;
    const kw: string[] = u.current?.keywords || u.keywords || [];
    if (kw.includes('stealth')) return false; // стелс невидим
    // Спящий агент Деуса в planted/armed — не блокирует Авангард.
    if (u.id === 'deus_inf_spyashy_agent' &&
        (u.sleeperState === 'planted' || u.sleeperState === 'armed')) {
      return false;
    }
    return true;
  });
  return blocking.length === 0;
}

// Можно ли Герою атаковать конкретный юнит? Учитывает Провокацию,
// Скрытность, untargetable. По диздоку §6.5.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function canHeroAttackUnit(state: Match, attackerIdx: 0 | 1, target: any): boolean {
  if (!target || !target.alive || target.revealed === false) return false;
  if (target.type === 'hero') return false; // используй canHeroAttackHero
  const kw: string[] = target.current?.keywords || target.keywords || [];
  if (kw.includes('stealth')) return false;
  if (target.untargetable?.byEnemyHero === true && !target.current?.silenced) return false;
  // Провокация: если есть non-stealth taunt-юниты — только их можно бить.
  const enemy = state.players[1 - attackerIdx];
  const enemyUnits = enemy.unitsOnField();
  const taunts = enemyUnits.filter((u: any) => {
    if (!u || !u.alive) return false;
    const ukw: string[] = u.current?.keywords || u.keywords || [];
    return ukw.includes('taunt') && !ukw.includes('stealth');
  });
  if (taunts.length > 0 && !kw.includes('taunt')) return false;
  return true;
}

// Мин. ход матча, на котором Action данной редкости активируется.
// (common=1, rare=3, epic=4, legendary=5 — диздок §6.6)
export function actionInitiationTurn(rarity: string): number {
  return engine.actionInitiationTurn(rarity);
}

// ────────── Активация Скрытного юнита (диздок §6.7) ──────────
//
// Стелс-юнит активируется в МОЕЙ hero-фазе (HERO_FIRST если я хожу
// первым, иначе HERO_SECOND). Игрок выбирает цель — стелс-юнит
// атакует один раз, теряет ключевое слово stealth.
//
// Авангард применяется и к стелс-атаке: если есть видимые враги на
// 1-й линии — Героя бить нельзя. Provocation тоже применяется.

// Список стелс-юнитов игрока, которых можно активировать сейчас.
// Возвращает массив unit-объектов с keyword='stealth', не спящих,
// revealed=true, у которых stealthUsed=false.
export function getActivatableStealth(state: Match, attackerIdx: 0 | 1): any[] {
  return engine.getActivatableStealth(state, attackerIdx) || [];
}

// Список целей для конкретного стелс-юнита (с учётом Авангарда/Taunt).
// Engine возвращает { units: Card[], heroAllowed: boolean } — UI рисует
// подсветку юнитов + (если heroAllowed) подсвечивает вражеский pbar.
export interface StealthTargets {
  units: any[];
  heroAllowed: boolean;
}
export function getStealthTargets(state: Match, attackerIdx: 0 | 1, attacker: any): StealthTargets {
  const r = engine.getStealthTargets(state, attackerIdx, attacker);
  return {
    units: Array.isArray(r?.units) ? r.units : [],
    heroAllowed: !!r?.heroAllowed
  };
}

// Активировать Скрытный юнит → атаковать выбранную цель. async — внутри
// идёт анимация удара.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function activateStealthUnit(state: Match, attackerIdx: 0 | 1, attacker: any, target: any): Promise<any> {
  return engine.activateStealthUnit(state, attackerIdx, attacker, target);
}

// ────────────── Активки Героев (Ф2 — диздок §6.4) ──────────────
//
// HERO_ACTIVE_MAP: heroId → { targetKind, available?, getTargets?,
// getDiscardChoices?, use }. targetKind задаёт UI-флоу выбора цели:
//   'none'      — применяется сразу, без цели;
//   'unit'      — выбрать один юнит на поле;
//   'unit_slot' — выбрать вражеский юнит + свою пустую ячейку (Кироса);
//   'unit_unit' — выбрать два своих юнита: источник и приёмник (Раст);
//   'discard'   — выбрать юнита из сброса (Лиза).

export type HeroActiveTargetKind = 'none' | 'unit' | 'unit_slot' | 'unit_unit' | 'discard';

export interface HeroActiveInfo {
  exists: boolean;        // активка Героя подключена к UI
  available: boolean;     // можно ли применить сейчас (Ф2, не использована, есть цели)
  needsTarget: boolean;   // targetKind !== 'none' (back-compat для BattleScreen)
  targetKind: HeroActiveTargetKind;
  reason?: string;        // почему недоступно (если !available / !exists)
}

export function getHeroActiveInfo(state: Match, playerIdx: 0 | 1): HeroActiveInfo {
  const NONE: HeroActiveInfo = { exists: false, available: false, needsTarget: false, targetKind: 'none' };
  const p = state.players?.[playerIdx];
  if (!p) return { ...NONE, reason: 'нет игрока' };
  const hero = p.hero;
  const heroId: string = hero?.id || '';
  const fn = HERO_ACTIVE_MAP[heroId];
  if (!fn) return { ...NONE, reason: 'активка не подключена' };
  const base = { exists: true, needsTarget: fn.targetKind !== 'none', targetKind: fn.targetKind };
  if (hero.hp <= 0) return { ...base, available: false, reason: 'Герой мёртв' };
  // БУГ.15: в движке два флага «активка использована» — heroActiveUsed
  // (старые Герои Деус/Синтетикс) и heroActiveUsedThisMatch (C.1-Герои:
  // Глобал, новые ЛивЛайф, нейтрал). Проверяем оба.
  if (p.heroActiveUsed || p.heroActiveUsedThisMatch) {
    return { ...base, available: false, reason: 'уже применена в матче' };
  }
  if (state.phase !== PHASE.PLACEMENT) {
    return { ...base, available: false, reason: 'только в Ф2 (Размещение)' };
  }
  // Доп. проверка конкретной активки (есть ли валидная цель и т.п.).
  if (fn.available) {
    let ok = false;
    try { ok = !!fn.available(state, playerIdx); }
    catch (e) { console.warn('[engine-bridge] hero available() failed:', e); }
    if (!ok) return { ...base, available: false, reason: 'нет подходящих целей' };
  }
  return { ...base, available: true };
}

// Валидные цели-юниты для активки текущего Героя (targetKind 'unit'/'unit_slot').
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getHeroActiveTargets(state: Match, playerIdx: 0 | 1): any[] {
  const heroId: string = state.players?.[playerIdx]?.hero?.id || '';
  const fn = HERO_ACTIVE_MAP[heroId];
  if (!fn?.getTargets) return [];
  try { return fn.getTargets(state, playerIdx) || []; }
  catch (e) { console.warn('[engine-bridge] getHeroActiveTargets failed:', e); return []; }
}

// Юнит из сброса для активки-воскрешения (Лиза). Возвращает оба сброса.
export interface HeroDiscardChoice {
  instanceId: number;
  name_ru: string;
  id: string;
  ownerIdx: 0 | 1;
  isEnemy: boolean;
}
export function getHeroActiveDiscardChoices(state: Match, playerIdx: 0 | 1): HeroDiscardChoice[] {
  const heroId: string = state.players?.[playerIdx]?.hero?.id || '';
  const fn = HERO_ACTIVE_MAP[heroId];
  if (!fn?.getDiscardChoices) return [];
  try { return (fn.getDiscardChoices(state, playerIdx) || []) as HeroDiscardChoice[]; }
  catch (e) { console.warn('[engine-bridge] getHeroActiveDiscardChoices failed:', e); return []; }
}

// Применить активку Героя. Возвращает результат с reason для
// visible-диагностики в UI. Форма target зависит от targetKind:
//   'none'      → target не нужен;
//   'unit'      → Card (юнит);
//   'unit_slot' → { card, line, slot } (Кироса);
//   'discard'   → { instanceId, ownerIdx } (Лиза).
export interface HeroActiveResult {
  ok: boolean;
  reason?: string;
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useHeroActive(state: Match, playerIdx: 0 | 1, target?: any): HeroActiveResult {
  const p = state.players?.[playerIdx];
  if (!p) return { ok: false, reason: 'нет игрока' };
  const heroId: string = p.hero?.id || '';
  const fn = HERO_ACTIVE_MAP[heroId];
  if (!fn) return { ok: false, reason: `активка не подключена (${heroId || '?'})` };
  if (fn.targetKind !== 'none' && target == null) {
    return { ok: false, reason: 'нужна цель' };
  }
  try {
    const r = fn.use(state, playerIdx, target);
    // Engine-функции активок возвращают { ok, reason?, ... } либо boolean.
    if (r && typeof r === 'object' && 'ok' in r) {
      return { ok: !!r.ok, reason: r.ok ? undefined : (r.reason || 'отказ движка') };
    }
    if (r === false) return { ok: false, reason: 'движок вернул false' };
    return { ok: true };
  } catch (e: any) {
    console.warn('[engine-bridge] hero active failed:', e);
    return { ok: false, reason: e?.message || 'исключение' };
  }
}

// АНИМ.10: async-вариант useHeroActive с diff-snapshot для анимаций
// heal/buff/summon/damage. Используется в PvE из BattleScreen.
// animator — клиентский Animator (тот же интерфейс что AnimRecorder).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function useHeroActiveWithAnim(
  state: Match, playerIdx: 0 | 1, target: any, animator: any
): Promise<HeroActiveResult> {
  const p = state.players?.[playerIdx];
  if (!p) return { ok: false, reason: 'нет игрока' };
  const heroId: string = p.hero?.id || '';
  const fn = HERO_ACTIVE_MAP[heroId];
  if (!fn) return { ok: false, reason: `активка не подключена (${heroId || '?'})` };
  if (fn.targetKind !== 'none' && target == null) {
    return { ok: false, reason: 'нужна цель' };
  }
  try {
    let r: any = null;
    await engine.runSyncWithDiff(state, animator, (s: any) => {
      r = fn.use(s, playerIdx, target);
    });
    if (r && typeof r === 'object' && 'ok' in r) {
      return { ok: !!r.ok, reason: r.ok ? undefined : (r.reason || 'отказ движка') };
    }
    if (r === false) return { ok: false, reason: 'движок вернул false' };
    return { ok: true };
  } catch (e: any) {
    console.warn('[engine-bridge] hero active anim failed:', e);
    return { ok: false, reason: e?.message || 'исключение' };
  }
}

// БУГ.15-fix2: HERO_ACTIVE_MAP — функции обёрнуты в lazy-стрелки.
// Раньше use/available захватывали engine.X ПО ЗНАЧЕНИЮ при создании
// объекта. Если в этот момент engine.X ещё undefined (порядок
// инициализации ESM-модулей) — функция навсегда оставалась undefined,
// и активка молча падала в catch. Lazy-стрелка читает engine.X в момент
// ВЫЗОВА — live binding гарантированно определён.
//
// targetKind: 'none' | 'unit' | 'unit_slot' | 'discard' — см. HeroActiveInfo.
// getTargets возвращает юниты-цели; getDiscardChoices — юнитов из сброса.
// use(state, playerIdx, target): target — Card / {card,line,slot} /
// {instanceId,ownerIdx} в зависимости от targetKind.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const HERO_ACTIVE_MAP: Record<string, {
  targetKind: HeroActiveTargetKind;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  available?: (state: Match, playerIdx: 0 | 1) => boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getTargets?: (state: Match, playerIdx: 0 | 1) => any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getDiscardChoices?: (state: Match, playerIdx: 0 | 1) => any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  use: (state: Match, playerIdx: 0 | 1, target?: any) => any;
}> = {
  // ── Деус ──
  'deus_tanaki': { targetKind: 'none',
    available: (s, p) => engine.tanakiActiveAvailable(s, p),
    use: (s, p) => engine.tanakiActive(s, p) },
  'deus_sina': { targetKind: 'unit',
    available: (s, p) => engine.sinaActiveAvailable(s, p),
    getTargets: (s, p) => { const me = s.players?.[p]; return me?.unitsOnField ? me.unitsOnField() : []; },
    use: (s, p, t) => engine.sinaActive(s, p, t) },
  'deus_nikosi': { targetKind: 'unit',
    available: (s, p) => engine.nikosiActiveAvailable(s, p),
    getTargets: (s, p) => {
      const enemy = s.players?.[1 - p];
      return enemy?.unitsOnField ? enemy.unitsOnField() : [];
    },
    use: (s, p, t) => engine.nikosiActive(s, p, t) },
  'deus_kirosa': { targetKind: 'unit_slot',
    available: (s, p) => engine.kirosaActiveAvailable(s, p),
    getTargets: (s, p) => engine.getKirosaTargets(s, p),
    use: (s, p, t) => engine.kirosaActive(s, p, t.card, { line: t.line, slot: t.slot }) },
  // ── Синтетикс ──
  'syn_bishor': { targetKind: 'unit',
    available: (s, p) => engine.bishorActiveAvailable(s, p),
    getTargets: (s, p) => engine.getBishorActiveTargets(s, p),
    use: (s, p, t) => engine.bishorActive(s, p, t) },
  'syn_uein': { targetKind: 'unit',
    available: (s, p) => engine.riperActiveAvailable(s, p),
    getTargets: (s, p) => engine.getRiperActiveTargets(s, p),
    use: (s, p, t) => engine.riperActive(s, p, t) },
  'syn_rast': { targetKind: 'unit_unit',
    available: (s, p) => engine.rastActiveAvailable(s, p),
    getTargets: (s, p) => engine.getRastActiveTargets(s, p),
    use: (s, p, t) => engine.rastActive(s, p, t.source, t.dest) },
  'syn_skvill': { targetKind: 'unit',
    available: (s, p) => engine.skvillActiveAvailable(s, p),
    getTargets: (s, p) => engine.getSkvillActiveTargets(s, p),
    use: (s, p, t) => engine.skvillActive(s, p, t) },
  // ── Глобал ──
  'global_mit': { targetKind: 'none',
    available: (s, p) => engine.polkovnikMitActiveAvailable(s, p),
    use: (s, p) => engine.polkovnikMitActive(s, p) },
  'global_iv': { targetKind: 'none',
    available: (s, p) => engine.mayorIvActiveAvailable(s, p),
    use: (s, p) => engine.mayorIvActive(s, p) },
  'global_li_pul': { targetKind: 'none',
    available: (s, p) => engine.strategLiPulActiveAvailable(s, p),
    use: (s, p) => engine.strategLiPulActive(s, p) },
  'global_rayna': { targetKind: 'unit',
    available: (s, p) => engine.komissarYarActiveAvailable(s, p),
    getTargets: (s, p) => engine.getKomissarYarActiveTargets(s, p),
    use: (s, p, t) => engine.komissarYarActive(s, p, { target: t }) },
  // ── Лив Лайф (v6) ──
  'livlife_ariana': { targetKind: 'none',
    available: (s, p) => engine.arianaActiveAvailable(s, p),
    use: (s, p) => engine.arianaActive(s, p) },
  'livlife_klod': { targetKind: 'none',
    available: (s, p) => engine.klodActiveAvailable(s, p),
    use: (s, p) => engine.klodActive(s, p) },
  'livlife_zayn': { targetKind: 'none',
    available: (s, p) => engine.zaynActiveAvailable(s, p),
    use: (s, p) => engine.zaynActive(s, p) },
  'livlife_lisa': { targetKind: 'discard',
    available: (s, p) => engine.lisaActiveAvailable(s, p),
    getDiscardChoices: (s, p) => engine.getLisaActiveDiscardChoices(s, p),
    use: (s, p, t) => engine.lisaActive(s, p, { targetInstanceId: t.instanceId, targetOwnerIdx: t.ownerIdx }) },
  // ── Нейтрал / Улица (БАГ-фикс Этап 56.56) ──
  // Активка «Лидер улицы» (3 урона выбранному вражескому юниту, 1×/матч) была
  // реализована в движке + экспортирована из match.js, но НЕ подключена в
  // HERO_ACTIVE_MAP → UI выдавал «активка не подключена». Подключаем.
  'neutral_lider_ulicy': { targetKind: 'unit',
    available: (s, p) => engine.liderUlicyActiveAvailable(s, p),
    getTargets: (s, p) => engine.getLiderUlicyActiveTargets(s, p),
    use: (s, p, t) => engine.liderUlicyActive(s, p, t) },
};

// ────────────── Bot AI ──────────────

// Мулиган бота — возвращает индексы карт на сброс.
export function botMulligan(state: Match, botIdx: 0 | 1 = 1): number[] {
  return bot.botMulligan(state, botIdx);
}

// Решение бота по размещению одной карты. Возвращает decision или null.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function botPlacement(state: Match, botIdx: 0 | 1 = 1): any {
  return bot.botPlacement(state, botIdx, state.rng);
}

// Применить решение бота (юнит → pendingPlacement, action → pendingAction).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function applyBotPlacementDecision(state: Match, botIdx: 0 | 1, decision: any): boolean {
  return bot.applyBotPlacementDecision(state, botIdx, decision, engine.setPendingPlacement);
}

// Решение по Моду (Синтетикс).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function botModPlay(state: Match, botIdx: 0 | 1 = 1): any {
  return bot.botModPlay(state, botIdx, state.rng);
}

// Решение по Влиянию (Деус).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function botInfluencePlay(state: Match, botIdx: 0 | 1 = 1): any {
  return bot.botInfluencePlay(state, botIdx, state.rng);
}

// Выбор цели атаки Героя ботом. null = пропуск.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function botHeroAttack(state: Match, botIdx: 0 | 1 = 1): any {
  return bot.botHeroAttack(state, botIdx, state.rng);
}

// ────────────── Карты Влияния (Деус) ──────────────

export interface InfluenceCardTpl {
  id: string;
  name_ru: string;
  faction: string;
  type: string;
  rarity: string;
  influence_cost: number | null;
  ability_short?: string | null;
  ability_text?: Record<string, string> | null;
}

// Шаблоны для UI (имя/cost/текст) по списку id.
export function getInfluenceCardTemplates(faction: string, ids: string[]): InfluenceCardTpl[] {
  return engine.getInfluenceCardTemplates(faction, ids) as InfluenceCardTpl[];
}

// Сколько раз игрок уже сыграл эту карту за матч.
export function getInfluencePlayCount(player: any, cardId: string): number {
  return engine.getInfluencePlayCount(player, cardId);
}

// Нужна ли цель (юнит-цель) для розыгрыша.
export function needsInfluenceTarget(cardId: string): boolean {
  return !!engine.influenceNeedsTarget(cardId);
}

// Список валидных целей для таргетной карты Влияния (Ликвидатор и т.п.).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getInfluenceTargets(state: Match, playerIdx: 0 | 1, cardId: string): any[] {
  try { return engine.getInfluenceTargets(state, playerIdx, cardId) || []; }
  catch { return []; }
}

// Можно ли сейчас сыграть карту (ok/reason).
export function canPlayInfluenceCard(state: Match, playerIdx: 0 | 1, cardId: string): { ok: boolean; reason?: string } {
  return engine.canPlayInfluenceCard(state, playerIdx, cardId);
}

// Сыграть карту. Для целевых нужен targetCard.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function playInfluenceCard(state: Match, playerIdx: 0 | 1, cardId: string, targetCard: any = null): { ok: boolean; reason?: string } {
  return engine.playInfluenceCard(state, playerIdx, cardId, targetCard);
}

// АНИМ.11: async-вариант с anim.influence + diff-snapshot. PvE-путь.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function playInfluenceCardWithAnim(
  state: Match, playerIdx: 0 | 1, cardId: string, targetCard: any, animator: any
): Promise<{ ok: boolean; reason?: string }> {
  if (!animator) {
    return playInfluenceCard(state, playerIdx, cardId, targetCard);
  }
  try {
    // Имя карты для overlay.
    let cardName = cardId;
    try {
      const tpls = engine.getInfluenceCardTemplates?.('deus', [cardId]) || [];
      if (tpls.length > 0 && (tpls[0]?.name_ru || tpls[0]?.name)) {
        cardName = tpls[0].name_ru || tpls[0].name || cardId;
      }
    } catch {}
    if (typeof animator.influence === 'function') {
      await animator.influence({ id: cardId, name_ru: cardName }, targetCard);
    }
    let r: any = null;
    await engine.runSyncWithDiff(state, animator, (s: any) => {
      r = engine.playInfluenceCard(s, playerIdx, cardId, targetCard);
    });
    if (r && typeof r === 'object' && 'ok' in r) {
      return { ok: !!r.ok, reason: r.ok ? undefined : (r.reason || 'отказ движка') };
    }
    return { ok: true };
  } catch (e: any) {
    return { ok: false, reason: e?.message || 'исключение' };
  }
}

export const INFLUENCE_PER_CARD_LIMIT: number = engine.INFLUENCE_PER_CARD_LIMIT ?? 2;

// ────────────── Helpers ──────────────

// Smoke-helper: создаёт матч с дефолтами, прогоняет мулиган и возвращает
// state в фазе PLACEMENT. Используется в HomeScreen для проверки что движок
// импортируется и работает в Vite-окружении.
export function createSmokeMatch(faction: MatchOptions['playerFaction'] = 'deus'): Match {
  const m = createMatch({
    playerName: 'Игрок',
    botName: 'Бот',
    playerFaction: faction,
    botFaction: 'deus',
    botRandom: true
  });
  finishMulligan(m);
  return m;
}

// БОЙ.1.1: создаёт матч ДЛЯ боя — НЕ прогоняет мулиган, чтобы UI мог
// показать пользователю стартовую руку и дать поменять карты.
// После этого вызов finishMulligan() запустит Ход 1 → Ф2 (PLACEMENT).
//
// heroId — выбранный Герой колоды игрока. Если null, движок возьмёт
// defaultHero(faction).
// playerDeck — массив шаблонов карт (24 шаблона из Deck Builder).
// Первые 5 → стартовая рука, остальные 19 → колода (shuffled). Если
// null — engine берёт buildDeckTemplates (рандом из всех карт фракции).
export function createMatchForBattle(
  faction: MatchOptions['playerFaction'] = 'deus',
  heroId?: string | null,
  playerName?: string,
  playerDeck?: unknown[] | null,
  league?: string | null
): Match {
  // Этап 66.2: PvE-соперник — случайный СИЛЬНЫЙ Герой корпы (по возможности
  // не той же, что у игрока) со своей синергия-колодой, а не всегда Деус с
  // фабричной. botRandom только если колоды для Героя нет.
  const botPick = botDecks.pickStrongBotHero(faction || null);
  const botDeck = botDecks.getBotDeck(botPick.heroId);
  const m = createMatch({
    playerName: playerName || 'Игрок',
    botName: `Бот ${botPick.faction}`,
    playerFaction: faction,
    playerHeroId: heroId || undefined,
    playerDeck: playerDeck || undefined,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    botFaction: botPick.faction as any,
    botHeroId: botPick.heroId,
    botDeck: botDeck || undefined,
    botRandom: !botDeck,
  });
  // Этап 68.2 (лесенка смягчена в 73): сложность PvE-бота по лиге игрока
  // (Трущобы=рандом → 30% → 50% → 65% → Оперативники и выше = полный smart).
  // Без лиги → leagueToBotLevel вернёт полный smart.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (m as any)._botLevel = bot.leagueToBotLevel(league || '');
  return m;
}

// Sandbox: создать матч с заданными Героями обеих сторон. Мулиган
// пропущен (finishMulligan вызван). Руки автоматически тянутся
// движком (по 5 карт + Ход 1 +1 = 6 в руке). Для sandbox юзер потом
// добавит свои карты через addCardToHand.
export function createSandboxMatch(
  factionA: MatchOptions['playerFaction'] = 'deus',
  heroAId: string,
  factionB: MatchOptions['playerFaction'] = 'deus',
  heroBId: string,
  noInitiation = false
): Match {
  const m = createMatch({
    playerName: 'Сторона A',
    botName: 'Сторона B',
    playerFaction: factionA,
    playerHeroId: heroAId,
    botFaction: factionB,
    botHeroId: heroBId
  });
  // SANDBOX: флаг «без инициации» на state — addCardToHand читает его и
  // ставит _sandboxNoInit на каждую добавляемую карту (эпики/леги активны
  // сразу, Действия не сгорают раньше срока). На реальные бои не влияет.
  (m as any)._sandboxNoInit = noInitiation;
  finishMulligan(m);
  return m;
}

// Sandbox: добавить карту в руку конкретного игрока. cardData — шаблон
// из cards-*.js или heroes-*.js. Создаёт Card instance через движок.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function addCardToHand(state: Match, playerIdx: 0 | 1, cardTemplate: any): boolean {
  try {
    // Динамический импорт Card class из движка.
    // eslint-disable-next-line @typescript-eslint/no-var-requires, @typescript-eslint/no-explicit-any
    const CardModule: any = (engine as any).__cardConstructor;
    let CardCls = CardModule;
    if (!CardCls) {
      // Fallback: использовать конструктор через клон существующей карты.
      // Каждая карта имеет prototype с methods. Берём из любой карты деки.
      const sample = state.players[0]?.deck?.[0] || state.players[1]?.deck?.[0];
      if (sample) CardCls = sample.constructor;
    }
    if (!CardCls) {
      console.warn('[sandbox] Card constructor not found, cannot add card');
      return false;
    }
    const card = new CardCls(cardTemplate);
    card.ownerId = playerIdx;
    // SANDBOX без инициации: помечаем карту — isSleeping вернёт false,
    // Действие не сгорит раньше срока.
    if ((state as any)._sandboxNoInit) card._sandboxNoInit = true;
    state.players[playerIdx].hand.push(card);
    return true;
  } catch (e) {
    console.warn('[sandbox] addCardToHand failed:', e);
    return false;
  }
}

// Sandbox: удалить карту из руки (если юзер ошибочно добавил).
export function removeCardFromHand(state: Match, playerIdx: 0 | 1, cardInstanceId: number): void {
  const p = state.players[playerIdx];
  const idx = p.hand.findIndex((c: any) => c.instanceId === cardInstanceId);
  if (idx !== -1) p.hand.splice(idx, 1);
}
